import {
  createTableDefinition,
  type TableDefinition,
} from '@/features/shared/components/tableSystem/tableDefinition';
import type { TestExecutionStatus } from '@flowqa/shared';

export type CloneSortDirection = 'asc' | 'desc';
export type CloneSortKey =
  | 'name'
  | 'suiteId'
  | 'latestExecutionTimestamp'
  | 'executionPassRate'
  | 'executionFrequencyPerDay'
  | 'executionAverageDurationMs'
  | 'executionPassRateDelta'
  | 'executionFrequencyPerDayDelta'
  | 'executionAverageDurationMsDelta';

const cloneDefaultSortKey: CloneSortKey = 'name';
const cloneDefaultSortDirection: CloneSortDirection = 'asc';

const cloneSortOptions: Array<{ key: CloneSortKey; label: string }> = [
  { key: 'name', label: 'Test' },
  { key: 'suiteId', label: 'Suite' },
  { key: 'latestExecutionTimestamp', label: 'Latest' },
  { key: 'executionPassRate', label: 'Pass Rate' },
  { key: 'executionPassRateDelta', label: 'Pass Rate Delta' },
  { key: 'executionFrequencyPerDay', label: 'Frequency' },
  { key: 'executionFrequencyPerDayDelta', label: 'Frequency Delta' },
  { key: 'executionAverageDurationMs', label: 'Average Duration' },
  { key: 'executionAverageDurationMsDelta', label: 'Average Duration Delta' },
];

const cloneSortFieldMap: Record<CloneSortKey, string> = {
  name: 'name',
  suiteId: 'slug',
  latestExecutionTimestamp: 'latestExecutionTimestamp',
  executionPassRate: 'executionPassRate',
  executionFrequencyPerDay: 'executionFrequencyPerDay',
  executionAverageDurationMs: 'executionAverageDurationMs',
  executionPassRateDelta: 'executionPassRateDelta',
  executionFrequencyPerDayDelta: 'executionFrequencyPerDayDelta',
  executionAverageDurationMsDelta: 'executionAverageDurationMsDelta',
};

const cloneStatusOptions: Array<{ value: TestExecutionStatus; label: string }> = [
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'errored', label: 'Errored' },
  { value: 'skipped', label: 'Skipped' },
];

export const testsTableDefinition: TableDefinition<CloneSortKey, TestExecutionStatus> =
  createTableDefinition({
    title: 'Tests',
    searchAriaLabel: 'Search tests, suites, tags',
    columns: [
      { field: 'name', label: 'Test', mandatory: true, defaultVisible: true, userVisible: true },
      { field: 'suiteId', label: 'Suite', defaultVisible: false, userVisible: false },
      { field: 'statusFilter', label: 'Status', defaultVisible: false, userVisible: false },
      {
        field: 'latestExecutionStatus',
        label: 'Latest Status',
        defaultVisible: true,
        userVisible: true,
      },
      {
        field: 'executionStatusCounts',
        label: 'Executions',
        defaultVisible: true,
        userVisible: true,
      },
      {
        field: 'latestExecutionDurationMs',
        label: 'Latest Duration',
        defaultVisible: false,
        userVisible: false,
      },
      {
        field: 'latestExecutionTimestamp',
        label: 'Latest Timestamp',
        defaultVisible: false,
        userVisible: false,
      },
      { field: 'metricsSummary', label: 'Metrics', defaultVisible: true, userVisible: true },
      {
        field: 'actions',
        label: 'Actions',
        defaultVisible: true,
        userVisible: true,
      },
    ],
    defaultSortKey: cloneDefaultSortKey,
    defaultSortDirection: cloneDefaultSortDirection,
    sortOptions: cloneSortOptions,
    sortFieldMap: cloneSortFieldMap,
    statusOptions: cloneStatusOptions,
  });
